import { Calculator, CheckCircle2, FileSignature, Mail, Plus, Search, Send, Sparkles } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import {
  convertProposalToAgreement,
  createAdminAgreement,
  createAdminProposal,
  createAgreementPaymentLink,
  getAdminAgreements,
  getAdminProposals,
  getProposalDashboardStats,
  sendAgreementEmail,
  sendProposalEmail,
  updateAdminAgreement,
  updateAdminProposal,
} from "../../lib/api";
import {
  addonCatalog,
  calculatePricing,
  formatCurrency,
  packageCatalog,
  selectableServices,
  selectedCatalogItems,
  serviceCatalog,
} from "../../lib/proposalCatalog";
import type { ProposalDashboardStats, ProposalRecord, ServiceAgreementRecord } from "../../lib/types";

const emptyProposal = {
  id: "",
  clientName: "",
  clientEmail: "",
  clientPhone: "",
  businessName: "",
  eventName: "",
  eventType: "",
  eventDate: "",
  eventLocation: "",
  proposalDate: new Date().toISOString().slice(0, 10),
  expirationDate: "",
  status: "Draft",
  selectedPackage: "",
  selectedServices: [] as string[],
  selectedAddons: [] as string[],
  discount: 0,
  customAdjustment: 0,
  customPackagePrice: 0,
  customAddOnPrice: 0,
  paymentOverride: "",
  clientSummary: "",
  timelineNotes: "",
  nextSteps: "Review the proposal, accept when ready, then Kreatethedate will prepare your Service Agreement.",
  proposalNotes: "",
};

type ProposalFormState = typeof emptyProposal;
const secondaryButtonClass = "focus-ring inline-flex items-center justify-center rounded-full border border-ktd-gold/30 bg-white px-4 py-2 text-xs font-extrabold text-ktd-rich transition hover:border-ktd-gold hover:bg-ktd-cream";
const proposalStatusOptions = ["Draft", "Sent", "Viewed", "Accepted", "Declined", "Expired", "Converted to Agreement"];
const agreementStatusOptions = ["Draft", "Sent", "Signed", "Voided", "Expired", "Needs Update"];

const emptyAgreement = {
  id: "",
  clientName: "",
  clientEmail: "",
  eventName: "",
  eventType: "",
  eventDate: "",
  eventLocation: "",
  effectiveDate: new Date().toISOString().slice(0, 10),
  status: "Draft",
  selectedPackage: "",
  selectedServices: [] as string[],
  selectedAddons: [] as string[],
  discount: 0,
  customAdjustment: 0,
  customPackagePrice: 0,
  customAddOnPrice: 0,
  paymentOverride: "",
  projectDescription: "",
  internalNotes: "",
};

type AgreementFormState = typeof emptyAgreement;

export function AdminProposalDashboardPage() {
  const [stats, setStats] = useState<ProposalDashboardStats | null>(null);

  useEffect(() => {
    void getProposalDashboardStats().then(setStats).catch(() => setStats(null));
  }, []);

  const cards = [
    ["Draft Proposals", stats?.draftProposals || 0, "/admin/proposals?status=Draft"],
    ["Sent Proposals", stats?.sentProposals || 0, "/admin/proposals?status=Sent"],
    ["Accepted Proposals", stats?.acceptedProposals || 0, "/admin/proposals?status=Accepted"],
    ["Declined Proposals", stats?.declinedProposals || 0, "/admin/proposals?status=Declined"],
    ["Draft Agreements", stats?.draftAgreements || 0, "/admin/service-agreements?status=Draft"],
    ["Sent Agreements", stats?.sentAgreements || 0, "/admin/service-agreements?status=Sent"],
    ["Signed Agreements", stats?.signedAgreements || 0, "/admin/agreement-records?status=Signed"],
    ["Expired Agreements", stats?.expiredAgreements || 0, "/admin/agreement-records?status=Expired"],
    ["Discovery Calls Scheduled", stats?.discoveryCallsScheduled || 0, "/admin/scheduling?schedulerType=discovery-call&status=Scheduled"],
    ["Kickoff Calls Scheduled", stats?.kickoffCallsScheduled || 0, "/admin/scheduling?schedulerType=kickoff-call&status=Scheduled"],
    ["Kickoff Calls Completed", stats?.kickoffCallsCompleted || 0, "/admin/scheduling?schedulerType=kickoff-call&status=Completed"],
    ["Revision Calls Scheduled", stats?.revisionCallsScheduled || 0, "/admin/scheduling?schedulerType=revision-review-call&status=Scheduled"],
    ["Final Review Calls Scheduled", stats?.finalReviewCallsScheduled || 0, "/admin/scheduling?schedulerType=final-delivery-review-call&status=Scheduled"],
  ];

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Proposals & Agreements"
        title="Create, track, and convert client proposals into service agreements."
        subtitle="Use this workspace for package proposals, custom add-ons, agreement records, and payment readiness before onboarding begins."
      />
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map(([label, value, href]) => (
          <Link key={String(label)} to={String(href)} className="ivory-card rounded-lg p-5 transition hover:shadow-gold">
            <Calculator className="size-7 text-[#8a6c14]" />
            <p className="mt-4 text-sm font-bold text-ktd-muted">{label}</p>
            <strong className="mt-2 block font-display text-4xl">{value}</strong>
          </Link>
        ))}
      </section>
      <section className="grid gap-4 md:grid-cols-2">
        <ActionCard title="Create a Proposal" href="/admin/proposals" copy="Build a package proposal with service panels, pricing totals, and acceptance terms." />
        <ActionCard title="Create a Service Agreement" href="/admin/service-agreements" copy="Generate an agreement from a proposal or create a blank agreement for manual review." />
        <ActionCard title="Signed Agreement Records" href="/admin/agreement-records" copy="Review signature status, marketing permission, pricing, deposits, and balance records." />
        <ActionCard title="Pricing Catalog" href="/admin/pricing-catalog" copy="Reference the finalized Kreatethedate services, packages, add-ons, and comparisons." />
        <ActionCard title="Scheduling" href="/admin/scheduling" copy="Manage discovery, proposal review, kickoff, revision, and final delivery scheduling links." />
      </section>
    </div>
  );
}

export function AdminProposalsPage() {
  const [form, setForm] = useState<ProposalFormState>(emptyProposal);
  const [proposals, setProposals] = useState<ProposalRecord[]>([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const pricing = useMemo(
    () =>
      calculatePricing({
        selectedPackage: form.selectedPackage,
        selectedServices: form.selectedServices,
        selectedAddons: form.selectedAddons,
        discount: form.discount,
        customAdjustment: form.customAdjustment,
        customPackagePrice: form.customPackagePrice,
        customAddOnPrice: form.customAddOnPrice,
        paymentOverride: form.paymentOverride,
      }),
    [form],
  );

  const selectedItems = selectedCatalogItems(form.selectedPackage, form.selectedServices, form.selectedAddons);

  const load = async () => setProposals(await getAdminProposals());
  useEffect(() => {
    void load();
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    const payload = { ...form, pricing, content: { clientSummary: form.clientSummary, timelineNotes: form.timelineNotes, nextSteps: form.nextSteps, proposalNotes: form.proposalNotes }, payment: { paymentOverride: form.paymentOverride } };
    try {
      const result = form.id ? await updateAdminProposal(form.id, payload) : await createAdminProposal(payload);
      setMessage(`Proposal saved for ${result.proposal.clientName}.`);
      setForm({ ...emptyProposal });
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save proposal.");
    }
  };

  const filtered = proposals.filter((proposal) =>
    (!statusFilter || proposal.status === statusFilter)
    && [proposal.clientName, proposal.clientEmail, proposal.eventName, proposal.status].join(" ").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Proposals" title="Build a polished Kreatethedate proposal." subtitle="Select packages, services, and add-ons; totals update automatically and only selected service descriptions appear." />
      <section className="grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
        <form onSubmit={save} className="ivory-card rounded-lg p-6 sm:p-8">
          <SectionTitle title="Client & Event" />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Client name" value={form.clientName} onChange={(value) => setForm({ ...form, clientName: value })} required />
            <Field label="Client email" value={form.clientEmail} onChange={(value) => setForm({ ...form, clientEmail: value })} required type="email" />
            <Field label="Client phone" value={form.clientPhone} onChange={(value) => setForm({ ...form, clientPhone: value })} />
            <Field label="Business/organization name" value={form.businessName} onChange={(value) => setForm({ ...form, businessName: value })} />
            <Field label="Event name" value={form.eventName} onChange={(value) => setForm({ ...form, eventName: value })} />
            <Field label="Event type" value={form.eventType} onChange={(value) => setForm({ ...form, eventType: value })} />
            <Field label="Event date" value={form.eventDate} onChange={(value) => setForm({ ...form, eventDate: value })} type="date" />
            <Field label="Event location" value={form.eventLocation} onChange={(value) => setForm({ ...form, eventLocation: value })} />
            <Field label="Proposal date" value={form.proposalDate} onChange={(value) => setForm({ ...form, proposalDate: value })} type="date" />
            <Field label="Expiration date" value={form.expirationDate} onChange={(value) => setForm({ ...form, expirationDate: value })} type="date" />
            <SelectField label="Internal proposal status" value={form.status} options={proposalStatusOptions} onChange={(value) => setForm({ ...form, status: value })} />
          </div>

          <SectionTitle title="Package Selection" />
          <select className="form-field focus-ring w-full rounded-lg px-4 py-3 text-sm font-bold" value={form.selectedPackage} onChange={(event) => setForm({ ...form, selectedPackage: event.target.value })}>
            <option value="">No package selected</option>
            {packageCatalog.map((item) => (
              <option key={item.id} value={item.id}>{item.name} - {item.priceLabel || formatCurrency(item.price)}</option>
            ))}
          </select>
          {form.selectedPackage === "custom-quote" && <Field label="Custom package price" type="number" value={String(form.customPackagePrice)} onChange={(value) => setForm({ ...form, customPackagePrice: Number(value) })} />}

          <SectionTitle title="Services" />
          <CheckboxGrid items={selectableServices} selected={form.selectedServices} onChange={(selectedServices) => setForm({ ...form, selectedServices })} />

          <SectionTitle title="Add-Ons & Enhancements" />
          <CheckboxGrid items={addonCatalog} selected={form.selectedAddons} onChange={(selectedAddons) => setForm({ ...form, selectedAddons })} />
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Field label="Discount" type="number" value={String(form.discount)} onChange={(value) => setForm({ ...form, discount: Number(value) })} />
            <Field label="Custom adjustment" type="number" value={String(form.customAdjustment)} onChange={(value) => setForm({ ...form, customAdjustment: Number(value) })} />
            <Field label="Custom add-on price" type="number" value={String(form.customAddOnPrice)} onChange={(value) => setForm({ ...form, customAddOnPrice: Number(value) })} />
          </div>

          <SectionTitle title="Proposal Content" />
          <TextArea label="Client/event summary" value={form.clientSummary} onChange={(value) => setForm({ ...form, clientSummary: value })} />
          <TextArea label="Timeline notes" value={form.timelineNotes} onChange={(value) => setForm({ ...form, timelineNotes: value })} />
          <TextArea label="What happens next" value={form.nextSteps} onChange={(value) => setForm({ ...form, nextSteps: value })} />
          <TextArea label="Custom payment arrangement" value={form.paymentOverride} onChange={(value) => setForm({ ...form, paymentOverride: value })} />

          <button className="focus-ring mt-6 inline-flex items-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold">
            <Plus className="size-4" />
            {form.id ? "Update Proposal" : "Save Proposal Draft"}
          </button>
          {message && <p className="mt-4 rounded-lg bg-white p-4 text-sm font-bold text-ktd-rich">{message}</p>}
        </form>

        <aside className="grid gap-5">
          <PricingSummary pricing={pricing} />
          <SelectedPanels items={selectedItems} />
        </aside>
      </section>

      <section className="ivory-card rounded-lg p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <SectionTitle title="Proposal Records" compact />
          {statusFilter ? <Link to="/admin/proposals" className="text-sm font-black text-[#8a6c14] underline">Clear {statusFilter} filter</Link> : null}
          <label className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search proposals" className="form-field focus-ring rounded-lg py-3 pl-11 pr-4 text-sm font-semibold" />
          </label>
        </div>
        <div className="mt-4 grid gap-3">
          {filtered.map((proposal) => (
            <RecordCard key={proposal.id} title={proposal.clientName} subtitle={`${proposal.eventName || "No event"} - ${proposal.status}`} amount={proposal.pricing.total} url={proposal.reviewUrl}>
              <button onClick={() => setForm(recordToForm(proposal))} className={secondaryButtonClass}>Edit</button>
              <button onClick={() => void sendProposalEmail(proposal.id).then((result) => setMessage(result.message)).then(load)} className={secondaryButtonClass}>Send</button>
              <button onClick={() => void convertProposalToAgreement(proposal.id).then((result) => setMessage(`Agreement created: ${result.agreement.agreementId}`)).then(load)} className={secondaryButtonClass}>Convert</button>
            </RecordCard>
          ))}
        </div>
      </section>
    </div>
  );
}

export function AdminServiceAgreementsPage() {
  const [agreements, setAgreements] = useState<ServiceAgreementRecord[]>([]);
  const [form, setForm] = useState<AgreementFormState>(emptyAgreement);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const load = async () => setAgreements(await getAdminAgreements());
  useEffect(() => {
    void load();
  }, []);

  const pricing = useMemo(
    () =>
      calculatePricing({
        selectedPackage: form.selectedPackage,
        selectedServices: form.selectedServices,
        selectedAddons: form.selectedAddons,
        discount: form.discount,
        customAdjustment: form.customAdjustment,
        customPackagePrice: form.customPackagePrice,
        customAddOnPrice: form.customAddOnPrice,
        paymentOverride: form.paymentOverride,
      }),
    [form],
  );
  const selectedItems = selectedCatalogItems(form.selectedPackage, form.selectedServices, form.selectedAddons);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setMessage("");
    const payload = {
      ...form,
      pricing,
      clientSummary: form.projectDescription,
      content: { clientSummary: form.projectDescription },
      payment: { paymentOverride: form.paymentOverride },
    };
    try {
      const result = form.id ? await updateAdminAgreement(form.id, payload) : await createAdminAgreement(payload);
      setMessage(`Agreement saved: ${result.agreement.agreementId}.`);
      setForm({ ...emptyAgreement });
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save agreement.");
    }
  };

  const createBlank = () => {
    setForm({ ...emptyAgreement });
    setMessage("Blank agreement ready. Add client details, services, terms, then save.");
  };

  const editAgreement = (agreement: ServiceAgreementRecord) => {
    setForm(recordToAgreementForm(agreement));
    setMessage(`Editing agreement ${agreement.agreementId}.`);
  };
  const filteredAgreements = agreements.filter((agreement) =>
    (!statusFilter || agreement.status === statusFilter)
    && [agreement.clientName, agreement.clientEmail, agreement.eventName, agreement.status].join(" ").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Service Agreements" title="Review, send, and prepare Kreatethedate Service Agreements." subtitle="Agreements can be generated from accepted proposals or created from scratch for custom projects." />
      <button onClick={createBlank} className="focus-ring w-fit rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold">New Blank Agreement</button>
      {message && <p className="ivory-card rounded-lg p-4 text-sm font-bold">{message}</p>}
      <section className="grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
        <form onSubmit={save} className="ivory-card rounded-lg p-6 sm:p-8">
          <SectionTitle title="Agreement Details" compact />
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Client name" value={form.clientName} onChange={(value) => setForm({ ...form, clientName: value })} required />
            <Field label="Client email" value={form.clientEmail} onChange={(value) => setForm({ ...form, clientEmail: value })} required type="email" />
            <Field label="Effective date" value={form.effectiveDate} onChange={(value) => setForm({ ...form, effectiveDate: value })} type="date" />
            <SelectField label="Agreement status" value={form.status} options={agreementStatusOptions} onChange={(value) => setForm({ ...form, status: value })} />
            <Field label="Event name" value={form.eventName} onChange={(value) => setForm({ ...form, eventName: value })} />
            <Field label="Event type" value={form.eventType} onChange={(value) => setForm({ ...form, eventType: value })} />
            <Field label="Event date" value={form.eventDate} onChange={(value) => setForm({ ...form, eventDate: value })} type="date" />
            <Field label="Event location" value={form.eventLocation} onChange={(value) => setForm({ ...form, eventLocation: value })} />
          </div>

          <SectionTitle title="Package Selection" />
          <select className="form-field focus-ring w-full rounded-lg px-4 py-3 text-sm font-bold" value={form.selectedPackage} onChange={(event) => setForm({ ...form, selectedPackage: event.target.value })}>
            <option value="">No package selected</option>
            {packageCatalog.map((item) => (
              <option key={item.id} value={item.id}>{item.name} - {item.priceLabel || formatCurrency(item.price)}</option>
            ))}
          </select>
          {form.selectedPackage === "custom-quote" && <Field label="Custom package price" type="number" value={String(form.customPackagePrice)} onChange={(value) => setForm({ ...form, customPackagePrice: Number(value) })} />}

          <SectionTitle title="Selected Services" />
          <CheckboxGrid items={selectableServices} selected={form.selectedServices} onChange={(selectedServices) => setForm({ ...form, selectedServices })} />

          <SectionTitle title="Add-Ons & Enhancements" />
          <CheckboxGrid items={addonCatalog} selected={form.selectedAddons} onChange={(selectedAddons) => setForm({ ...form, selectedAddons })} />
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Field label="Discount" type="number" value={String(form.discount)} onChange={(value) => setForm({ ...form, discount: Number(value) })} />
            <Field label="Custom adjustment" type="number" value={String(form.customAdjustment)} onChange={(value) => setForm({ ...form, customAdjustment: Number(value) })} />
            <Field label="Custom add-on price" type="number" value={String(form.customAddOnPrice)} onChange={(value) => setForm({ ...form, customAddOnPrice: Number(value) })} />
          </div>

          <SectionTitle title="Agreement Notes" />
          <TextArea label="Project description" value={form.projectDescription} onChange={(value) => setForm({ ...form, projectDescription: value })} />
          <TextArea label="Custom payment arrangement" value={form.paymentOverride} onChange={(value) => setForm({ ...form, paymentOverride: value })} />
          <TextArea label="Internal notes" value={form.internalNotes} onChange={(value) => setForm({ ...form, internalNotes: value })} />

          <button className="focus-ring mt-6 inline-flex items-center gap-2 rounded-full bg-ktd-black px-5 py-3 text-sm font-extrabold text-ktd-softGold">
            <FileSignature className="size-4" />
            {form.id ? "Update Agreement" : "Save Agreement Draft"}
          </button>
        </form>

        <aside className="grid gap-5">
          <PricingSummary pricing={pricing} />
          <SelectedPanels items={selectedItems} />
        </aside>
      </section>
      <section className="ivory-card rounded-lg p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <SectionTitle title="Agreement Records" compact />
          {statusFilter ? <Link to="/admin/service-agreements" className="text-sm font-black text-[#8a6c14] underline">Clear {statusFilter} filter</Link> : null}
          <label className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search agreements" className="form-field focus-ring rounded-lg py-3 pl-11 pr-4 text-sm font-semibold" />
          </label>
        </div>
        <div className="mt-4">
          <AgreementList agreements={filteredAgreements} onRefresh={load} onMessage={setMessage} onEdit={editAgreement} />
        </div>
      </section>
    </div>
  );
}

export function AdminAgreementRecordsPage() {
  const [agreements, setAgreements] = useState<ServiceAgreementRecord[]>([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  useEffect(() => {
    void getAdminAgreements().then(setAgreements);
  }, []);
  const filtered = agreements.filter((agreement) =>
    (!statusFilter || agreement.status === statusFilter)
    && [agreement.clientName, agreement.clientEmail, agreement.eventName, agreement.status].join(" ").toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Signed Agreements / Agreement Records" title="Track signature status, payment readiness, and agreement records." subtitle="Use this section as the internal source of truth for signed agreements and agreement records." />
      {statusFilter ? <Link to="/admin/agreement-records" className="text-sm font-black text-[#8a6c14] underline">Clear {statusFilter} filter</Link> : null}
      <label className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ktd-muted" />
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search agreement records" className="form-field focus-ring w-full rounded-lg py-3 pl-11 pr-4 text-sm font-semibold" />
      </label>
      <AgreementList agreements={filtered} onRefresh={async () => setAgreements(await getAdminAgreements())} onMessage={() => undefined} />
    </div>
  );
}

export function AdminPricingCatalogPage() {
  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Pricing Catalog" title="Kreatethedate service selection reference." subtitle="Official package, service, add-on, and comparison reference for proposal creation." />
      <CatalogTable title="Packages" items={packageCatalog} />
      <CatalogTable title="Services" items={selectableServices} />
      <CatalogTable title="Add-ons and Enhancements" items={addonCatalog} />
    </div>
  );
}

function AgreementList({
  agreements,
  onRefresh,
  onMessage,
  onEdit,
}: {
  agreements: ServiceAgreementRecord[];
  onRefresh: () => Promise<void>;
  onMessage: (message: string) => void;
  onEdit?: (agreement: ServiceAgreementRecord) => void;
}) {
  return (
    <section className="grid gap-3">
      {agreements.map((agreement) => (
        <RecordCard key={agreement.id} title={agreement.clientName} subtitle={`${agreement.agreementId} - ${agreement.status}`} amount={agreement.pricing.total} url={agreement.signingUrl}>
          {onEdit ? <button onClick={() => onEdit(agreement)} className={secondaryButtonClass}>Edit</button> : null}
          <button onClick={() => void sendAgreementEmail(agreement.id).then((result) => onMessage(result.message)).then(onRefresh)} className={secondaryButtonClass}>Send</button>
          <button onClick={() => void createAgreementPaymentLink(agreement.id).then((result) => onMessage(result.paymentUrl ? `${result.message} ${result.paymentUrl}` : result.message)).then(onRefresh)} className={secondaryButtonClass}>Payment Link</button>
        </RecordCard>
      ))}
    </section>
  );
}

function PricingSummary({ pricing }: { pricing: ReturnType<typeof calculatePricing> }) {
  return (
    <article className="rounded-lg bg-ktd-black p-6 text-white shadow-luxe">
      <Calculator className="size-8 text-ktd-softGold" />
      <h2 className="mt-4 font-display text-3xl font-bold">Project Total</h2>
      <p className="mt-3 font-display text-5xl font-bold text-ktd-softGold">{formatCurrency(pricing.total)}</p>
      <dl className="mt-5 grid gap-2 text-sm">
        <SummaryRow label="Package" value={formatCurrency(pricing.packagePrice)} />
        <SummaryRow label="Services" value={formatCurrency(pricing.serviceTotal)} />
        <SummaryRow label="Add-ons" value={formatCurrency(pricing.addonTotal)} />
        <SummaryRow label="Discount" value={`-${formatCurrency(pricing.discount)}`} />
        <SummaryRow label="Custom adjustment" value={formatCurrency(pricing.customAdjustment)} />
        <SummaryRow label="Deposit due" value={formatCurrency(pricing.depositDue)} />
        <SummaryRow label="Remaining balance" value={formatCurrency(pricing.remainingBalance)} />
      </dl>
      <p className="mt-4 rounded-lg bg-white/10 p-4 text-sm font-semibold leading-6 text-white/76">{pricing.paymentTerms}</p>
    </article>
  );
}

function SelectedPanels({ items }: { items: Array<{ id: string; name: string; includes: string[]; description: string; price: number; priceLabel?: string }> }) {
  return (
    <section className="grid gap-4">
      {items.length === 0 ? <div className="ivory-card rounded-lg p-6 text-sm font-bold text-ktd-muted">Select a package, service, or add-on to show included details.</div> : null}
      {items.map((item) => (
        <article key={item.id} className="ivory-card rounded-lg p-5">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6c14]">{item.priceLabel || formatCurrency(item.price)}</p>
          <h3 className="mt-1 font-display text-2xl font-bold">{item.name}</h3>
          <p className="mt-2 text-sm leading-6 text-ktd-muted">{item.description}</p>
          <ul className="mt-4 grid gap-2 text-sm font-semibold text-ktd-rich">
            {item.includes.map((included) => <li key={included}>- {included}</li>)}
          </ul>
        </article>
      ))}
    </section>
  );
}

function CatalogTable({ title, items }: { title: string; items: typeof serviceCatalog }) {
  return (
    <section className="ivory-card rounded-lg p-6">
      <SectionTitle title={title} compact />
      <div className="mt-4 grid gap-3">
        {items.map((item) => (
          <article key={item.id} className="rounded-lg border border-ktd-gold/20 bg-white p-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="font-display text-2xl font-bold">{item.name} {item.mostPopular ? <span className="text-sm text-[#8a6c14]">Most Popular</span> : null}</h3>
                <p className="mt-1 text-sm text-ktd-muted">{item.description}</p>
              </div>
              <strong className="font-display text-2xl">{item.priceLabel || formatCurrency(item.price)}</strong>
            </div>
            <p className="mt-3 text-sm font-semibold text-ktd-rich">{item.includes.join(" | ")}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function RecordCard({ title, subtitle, amount, url, children }: { title: string; subtitle: string; amount: number; url: string; children: ReactNode }) {
  return (
    <article className="rounded-lg border border-ktd-gold/20 bg-white p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="font-display text-2xl font-bold">{title}</h3>
          <p className="mt-1 text-sm font-semibold text-ktd-muted">{subtitle}</p>
          <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-sm font-black text-[#8a6c14] underline">{url}</a>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <strong className="mr-2 font-display text-2xl">{formatCurrency(amount)}</strong>
          {children}
        </div>
      </div>
    </article>
  );
}

function CheckboxGrid({ items, selected, onChange }: { items: typeof serviceCatalog; selected: string[]; onChange: (value: string[]) => void }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => (
        <label key={item.id} className="flex items-start gap-3 rounded-lg border border-ktd-gold/20 bg-white p-4 text-sm font-bold">
          <input
            type="checkbox"
            checked={selected.includes(item.id)}
            onChange={(event) => onChange(event.target.checked ? [...selected, item.id] : selected.filter((id) => id !== item.id))}
            className="mt-1 size-4 accent-ktd-gold"
          />
          <span>{item.name} <span className="text-ktd-muted">({item.priceLabel || formatCurrency(item.price)})</span></span>
        </label>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <input value={value} onChange={(event) => onChange(event.target.value)} type={type} required={required} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
    </label>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold">
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="mt-4 grid gap-2 text-sm font-extrabold text-ktd-rich">
      {label}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-semibold" />
    </label>
  );
}

function SectionTitle({ title, compact = false }: { title: string; compact?: boolean }) {
  return <h2 className={`${compact ? "" : "mt-7"} font-display text-3xl font-bold text-ktd-rich`}>{title}</h2>;
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-t border-white/10 py-2">
      <dt className="text-white/64">{label}</dt>
      <dd className="font-black text-white">{value}</dd>
    </div>
  );
}

function ActionCard({ title, href, copy }: { title: string; href: string; copy: string }) {
  return (
    <Link to={href} className="ivory-card rounded-lg p-6 transition hover:shadow-gold">
      <Sparkles className="size-8 text-[#8a6c14]" />
      <h2 className="mt-4 font-display text-3xl font-bold">{title}</h2>
      <p className="mt-3 text-sm leading-6 text-ktd-muted">{copy}</p>
    </Link>
  );
}

function recordToForm(proposal: ProposalRecord): ProposalFormState {
  return {
    ...emptyProposal,
    id: proposal.id,
    clientName: proposal.clientName,
    clientEmail: proposal.clientEmail,
    clientPhone: proposal.clientPhone || "",
    businessName: proposal.businessName || "",
    eventName: proposal.eventName || "",
    eventType: proposal.eventType || "",
    eventDate: proposal.eventDate || "",
    eventLocation: proposal.eventLocation || "",
    proposalDate: proposal.proposalDate || "",
    expirationDate: proposal.expirationDate || "",
    status: proposal.status,
    selectedPackage: proposal.selectedPackage || "",
    selectedServices: proposal.selectedServices,
    selectedAddons: proposal.selectedAddons,
    discount: proposal.pricing.discount || 0,
    customAdjustment: proposal.pricing.customAdjustment || 0,
    clientSummary: String(proposal.content.clientSummary || ""),
    timelineNotes: String(proposal.content.timelineNotes || ""),
    nextSteps: String(proposal.content.nextSteps || ""),
    paymentOverride: String(proposal.payment.paymentOverride || ""),
  };
}

function recordToAgreementForm(agreement: ServiceAgreementRecord): AgreementFormState {
  return {
    ...emptyAgreement,
    id: agreement.id,
    clientName: agreement.clientName,
    clientEmail: agreement.clientEmail,
    eventName: agreement.eventName || "",
    eventType: agreement.eventType || "",
    eventDate: agreement.eventDate || "",
    eventLocation: agreement.eventLocation || "",
    effectiveDate: agreement.effectiveDate || "",
    status: agreement.status,
    selectedPackage: agreement.selectedPackage || "",
    selectedServices: agreement.selectedServices,
    selectedAddons: agreement.selectedAddons,
    discount: agreement.pricing.discount || 0,
    customAdjustment: agreement.pricing.customAdjustment || 0,
    projectDescription: agreement.projectDescription || "",
    paymentOverride: String(agreement.payment.paymentOverride || ""),
    internalNotes: agreement.internalNotes || "",
  };
}
